import { stateGo } from 'redux-ui-router';
import { kebabCase } from 'lodash';
import { CREDIT_CARD_FIELDS, CREDIT_CARD_REDIRECT_URI } from '../constants';
import { memberStorage } from 'utils';
import { selectMember, selectSubscription } from 'logic/member';
import {
  checkStatus,
  fetchProducts,
  getWeeklyProductText,
  reset,
  selectProduct,
  selectProductsByType,
  selectProductBySlug,
  setup,
  setupChargifyError,
  update,
  updateChargifyData,
  verifyCreditCard,
} from 'logic/signup';

import { SIGNUP_TEXT } from '../constants';

export const CreditCardUpdate = {
  template: `
    <div class="signup">
      <main class="main-content theme-mid">
        <div class="container align-items-center justify-content-center py-4"
          ng-switch="$ctrl.action">
          <div ng-switch-when="verify">
            <signup-verifying
              is-loading="$ctrl.isLoading"
              message="$ctrl.message">
              Verifying Credit Card
            </signup-verifying>
          </div>
          <div class="row" ng-switch-default>
            <div class="col-md-8 mx-auto pb-4">
              <header class="text-center text-white">
                <div ng-show="$ctrl.action === 'resume' && $ctrl.subscription.status === 'cancelled'">
                  <h1 class="h2 text-white">
                    Welcome back {{ $ctrl.member.firstName }}
                  </h1>
                  <p class="mb-4">
                    Start Vooming again!<br>
                    Please update your billing details below.
                  </p>
                </div>
                <div ng-hide="$ctrl.action === 'resume'">
                  <h1 class="h2 text-white">
                    Update Credit Card
                  </h1>
                  <p class="mb-4">
                    Please update your billing details below.
                  </p>
                </div>
              </header>
              <div class="theme-light p-3">
                <div class="signup-content bg-white py-3 py-lg-5"
                  scroll-top-on-change="$ctrl.message">
                  <div class="px-3 px-md-5">
                    <alert type="danger" ng-show="$ctrl.message">
                      <div ng-bind-html="$ctrl.message"></div>
                    </alert>
                    <alert type="danger" ng-show="$ctrl.isDisabled">
                      Sorry team! Due to site maintenance, updates cannot be
                      accepted right now. Please try again in an hour.
                    </alert>
                  </div>
                  <div class="px-3 px-md-5"
                    ng-show="$ctrl.action === 'resume' && $ctrl.subscription.isWeekly && !$ctrl.message">
                    <alert type="danger"
                      ng-show="$ctrl.subscription.status === 'suspended'">
                      We had a problem processing one or more of your Voome
                      payments. Please update your credit card details below. If
                      problems persist, please contact us at
                      <a href="mailto:support@voome.com.au">support@voome.com.au</a>.
                    </alert>
                  </div>
                  <signup-selector-weekly
                    is-active="true"
                    product="$ctrl.product"
                    products="$ctrl.products"
                    text="$ctrl.text"
                    on-change="$ctrl.onChangeWeekly(product)"
                    on-select-bundle="$ctrl.onChangeWeekly({ name: 'Voome Bundle' })">
                  </signup-selector-weekly>
                  <div ng-hide="$ctrl.isDisabled">
                    <div class="px-3 px-md-5">
                      <signup-form-billing
                        is-loading="$ctrl.isLoading"
                        chargify-data="$ctrl.chargifyData"
                        chargify-fields="$ctrl.chargifyFields"
                        chargify-subscription-id="$ctrl.subscription.chargifySubscriptionId"
                        product="$ctrl.product"
                        title="Billing Details"
                        on-cancel="$ctrl.onCancel()"
                        on-update="$ctrl.updateChargifyData(key, value)">
                      </signup-form-billing>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>`,

  controller($ngRedux) {
    'ngInject';

    return {
      $onInit() {
        const connect = $ngRedux.connect(this.mapState, this.mapDispatch());
        this.disconnect = connect(this);

        // Set the fields to submit
        this.chargifyFields = CREDIT_CARD_FIELDS;

        // Get the params and set the sction type
        const { currentParams } = $ngRedux.getState().router;
        this.action = currentParams.action;

        // Handle the action
        this.action === 'verify'
          ? this.handleVerify(currentParams)
          : this.handleSetUp(currentParams);

        // Fetch all products.
        this.fetchProducts();
      },

      $onDestroy() {
        this.disconnect();
      },

      runSetUp(productId) {
        const { email, firstName, lastName } = this.member;
        const { chargifySubscriptionId } = this.subscription;
        const redirectUri = `${CREDIT_CARD_REDIRECT_URI}?voome_product_id=${productId}`;

        this.checkStatus();

        // Update chargify data with product id
        this.setup({
          voomeProductId: productId,
          chargifyData: {
            email,
            firstName,
            lastName,
            redirectUri,
            subscriptionId: chargifySubscriptionId,
          },
        });
      },

      handleSetUp({ slug }) {
        // Init form with the current subscription's product
        const { voomeProductId } = this.subscription;
        const product = selectProductBySlug(this.signup, slug);
        const productId = product ? product.id : voomeProductId;

        if (this.chargifyData && !this.chargifyData.voomeProductId) {
          this.runSetUp(productId);
        }
      },

      handleVerify(params) {
        const { voomeProductId } = memberStorage.get();
        const verifyProductId = parseInt(params.voome_product_id);

        if (voomeProductId !== verifyProductId) {
          memberStorage.set({ voomeProductId: verifyProductId });
        }

        params.status_code === '200'
          ? this.verifyCreditCard(params, { name: 'dashboard' })
          : this.setupChargifyError(params);
      },

      onCancel() {
        if (this.action === 'update') {
          this.stateGo('profile');
        }

        this.reset();
      },

      onChangeWeekly({ name }) {
        const toggle = kebabCase(name);
        const slug =
          toggle === 'voome-bundle'
            ? toggle
            : toggle === 'voome-workouts'
              ? 'voome-nutrition'
              : 'voome-workouts';
        const product = selectProductBySlug(this.signup, slug);

        // Update signup state with new product id
        this.runSetUp(product.id);

        // Update page
        this.stateGo('profileCreditCard', { slug });
      },

      mapState({ member, signup }) {
        const {
          chargifyData,
          isDisabled,
          isLoading,
          message,
          step,
          type,
        } = signup;
        const subscription = selectSubscription(member);

        const { slug } = $ngRedux.getState().router.currentParams;
        const product = slug
          ? selectProductBySlug(signup, slug)
          : selectProduct(signup, subscription.voomeProductId) || {};

        return {
          chargifyData,
          isDisabled,
          isLoading,
          message,
          slug,
          step,
          signup,
          subscription,
          product,
          member: selectMember(member),
          products: selectProductsByType(signup, type),
          productsText: getWeeklyProductText(product),
          text: SIGNUP_TEXT['weekly'],
        };
      },

      mapDispatch() {
        return {
          checkStatus,
          fetchProducts,
          reset,
          setup,
          setupChargifyError,
          stateGo,
          updateChargifyData,
          verifyCreditCard,
        };
      },
    };
  },
};



// WEBPACK FOOTER //
// ./src/containers/CreditCardUpdate.js